// tab/src/services/api.ts
// OnRounds Phase 2 - API Service

import axios, { AxiosInstance, AxiosError } from 'axios';
import * as microsoftTeams from '@microsoft/teams-js';

// Types defined inline
interface ClockOnRequest { teamId: string; roleId: string; userId?: string; userDisplayName?: string; plannedEndTime?: string; }
interface ClockOnResponse { success: boolean; shift: any; role: any; pendingMessages: any[]; }
interface ClockOffRequest { shiftId: string; notes?: string; }
interface ClockOffResponse { success: boolean; shift: any; handoverRequired: boolean; unhandledMessages: any[]; }
interface SendMessageRequest { teamId: string; toRoleId: string; subject: string; body: string; priority?: string; fromUserId?: string; fromUserName?: string; }
interface SendMessageResponse { message: any; }
interface RoleDirectoryResponse { roles: any[]; }
interface SupervisorDashboard { teamName: string; summary: any; roles: any[]; recentActivity: any[]; }
interface StaffImportRequest { teamId: string; orgId?: string; csvData: string; updateExisting?: boolean; }
interface StaffImportResult { success: boolean; imported: number; updated?: number; errors: any[]; }
interface AuditSearchRequest { teamId: string; startDate: string; endDate: string; actions?: string[]; entityTypes?: string[]; action?: string; entityType?: string; searchTerm?: string; page?: number; pageSize?: number; }
interface AuditSearchResponse { entries: any[]; results?: any[]; totalCount: number; }
interface Role { id: string; name: string; description?: string; category?: string; color?: string; requiredQualifications?: string[]; isActive?: boolean; }
interface Shift { id: string; userId: string; roleId: string; teamId: string; status: string; clockOnTime: string; clockOffTime?: string; plannedEndTime?: string; }
interface Message { 
  id: string; 
  subject: string; 
  body: string; 
  fromUserId: string; 
  fromUserName: string; 
  toRoleId: string; 
  toRoleName: string; 
  priority: 'routine' | 'urgent' | 'critical'; 
  status: string; 
  createdAt: string; 
  readAt: string | null;
  handoverHistory?: Array<{
    timestamp: string;
    fromUserName: string;
    toUserName: string;
    acknowledged: boolean;
    acknowledgedAt: string | null;
  }>;
}
interface User { id: string; name: string; email: string; displayName?: string; qualifications?: string[]; isAdmin?: boolean; isSupervisor?: boolean; orgId?: string; }
interface Team { id: string; name: string; orgId: string; organisationId?: string; }

class ApiService {
  private client: AxiosInstance;
  private token: string | null = null;

  constructor() {
    const baseURL = process.env.REACT_APP_API_URL || '/api';
    
    this.client = axios.create({
      baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      async (config) => {
        const token = await this.getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          // Token expired - clear and retry
          this.token = null;
        }
        return Promise.reject(this.formatError(error));
      }
    );
  }

  private async getToken(): Promise<string | null> {
    if (this.token) return this.token;

    try {
      // Get SSO token from Teams
      const result = await microsoftTeams.authentication.getAuthToken();
      this.token = result;
      return result;
    } catch (err) {
      console.error('Failed to get auth token:', err);
      
      // For development, use a mock token
      if (process.env.NODE_ENV === 'development') {
        this.token = 'dev-token';
        return this.token;
      }
      
      return null;
    }
  }

  private formatError(error: AxiosError): Error {
    if (error.response?.data) {
      const data = error.response.data as any;
      return new Error(data.error || data.message || 'An error occurred');
    }
    if (error.message) {
      return new Error(error.message);
    }
    return new Error('An unexpected error occurred');
  }

  // ============================================
  // SHIFTS
  // ============================================

  async clockOn(request: ClockOnRequest): Promise<ClockOnResponse> {
    const { data } = await this.client.post<ClockOnResponse>('/shifts/clockon', request);
    return data;
  }

  async clockOff(request: ClockOffRequest): Promise<ClockOffResponse> {
    const { data } = await this.client.post<ClockOffResponse>('/shifts/clockoff', request);
    return data;
  }

  async getCurrentShift(teamId: string, userId: string): Promise<{ shift: Shift | null; role: Role | null; clockedOn: boolean }> {
    const { data } = await this.client.get(`/shifts/current?teamId=${teamId}&userId=${userId}`);
    return data;
  }

  async getActiveShifts(teamId: string): Promise<{ shifts: Shift[] }> {
    const { data } = await this.client.get(`/shifts/active/${teamId}`);
    return data;
  }

  async getShiftHistory(teamId: string, limit?: number): Promise<{ shifts: Shift[] }> {
    const { data } = await this.client.get(`/shifts/history?teamId=${teamId}&limit=${limit || 50}`);
    return data;
  }

  // ============================================
  // ROLES
  // ============================================

  async getRoles(teamId: string): Promise<{ roles: Role[] }> {
    const { data } = await this.client.get(`/roles/${teamId}`);
    return data;
  }

  async getRoleDirectory(teamId: string): Promise<RoleDirectoryResponse> {
    const { data } = await this.client.get(`/roles/${teamId}/directory`);
    return data;
  }

  async canTakeRole(teamId: string, roleId: string): Promise<{ canTake: boolean; reason?: string }> {
    const { data } = await this.client.get(`/roles/${teamId}/${roleId}/can-take`);
    return data;
  }

  async createRole(teamId: string, role: Partial<Role>): Promise<{ role: Role }> {
    const { data } = await this.client.post(`/roles/${teamId}`, role);
    return data;
  }

  async updateRole(teamId: string, roleId: string, updates: Partial<Role>): Promise<{ role: Role }> {
    const { data } = await this.client.put(`/roles/${teamId}/${roleId}`, updates);
    return data;
  }

  async deleteRole(teamId: string, roleId: string): Promise<{ role: Role }> {
    const { data } = await this.client.delete(`/roles/${teamId}/${roleId}`);
    return data;
  }

  // ============================================
  // MESSAGES
  // ============================================

  async sendMessage(request: SendMessageRequest): Promise<SendMessageResponse> {
    const { data } = await this.client.post<SendMessageResponse>('/messages/send', request);
    return data;
  }

  async getInbox(teamId: string, roleId: string, includeRead = false): Promise<{ messages: Message[]; totalCount: number }> {
    const { data } = await this.client.get(`/messages/inbox/${teamId}/${roleId}?includeRead=${includeRead}`);
    return data;
  }

  async getSentMessages(teamId: string, userId: string): Promise<{ messages: Message[] }> {
    const { data } = await this.client.get(`/messages/sent?teamId=${teamId}&userId=${userId}`);
    return data;
  }

  async markAsRead(teamId: string, messageId: string): Promise<{ message: Message }> {
    const { data } = await this.client.put(`/messages/${teamId}/${messageId}/read`);
    return data;
  }

  async acknowledgeHandover(teamId: string, messageId: string): Promise<{ message: Message }> {
    const { data } = await this.client.put(`/messages/${teamId}/${messageId}/acknowledge-handover`);
    return data;
  }

  async getPendingHandover(teamId: string, roleId: string): Promise<{ messages: Message[]; count: number }> {
    const { data } = await this.client.get(`/messages/${teamId}/${roleId}/pending-handover`);
    return data;
  }

  async getMessage(teamId: string, messageId: string): Promise<{ message: Message }> {
    const { data } = await this.client.get(`/messages/${teamId}/${messageId}`);
    return data;
  }

  // ============================================
  // ADMIN
  // ============================================

  async importStaff(request: StaffImportRequest): Promise<StaffImportResult> {
    const { data } = await this.client.post<StaffImportResult>('/admin/import-staff', request);
    return data;
  }

  async downloadCSVTemplate(): Promise<Blob> {
    const { data } = await this.client.get('/admin/csv-template', {
      responseType: 'blob',
    });
    return data;
  }

  async getSupervisorDashboard(teamId: string): Promise<SupervisorDashboard> {
    const { data } = await this.client.get<SupervisorDashboard>(`/admin/dashboard/${teamId}`);
    return data;
  }

  async getTeamRoster(teamId: string): Promise<{ members: User[]; qualificationCoverage: Record<string, number> }> {
    const { data } = await this.client.get(`/admin/roster/${teamId}`);
    return data;
  }

  async addTeamMember(teamId: string, userId: string): Promise<void> {
    await this.client.post(`/admin/roster/${teamId}/members`, { userId });
  }

  async removeTeamMember(teamId: string, userId: string): Promise<void> {
    await this.client.delete(`/admin/roster/${teamId}/members/${userId}`);
  }

  async getUsers(): Promise<{ users: User[] }> {
    const { data } = await this.client.get('/admin/users');
    return data;
  }

  async updateUser(userId: string, updates: Partial<User>): Promise<{ user: User }> {
    const { data } = await this.client.put(`/admin/users/${userId}`, updates);
    return data;
  }

  async getTeams(userId?: string): Promise<{ teams: Team[] }> {
    const url = userId ? `/teams?userId=${userId}` : '/admin/teams';
    const { data } = await this.client.get(url);
    return data;
  }

  async getCurrentUser(azureAdId: string): Promise<{ user: User | null }> {
    const { data } = await this.client.get(`/users/me?azureAdId=${azureAdId}`);
    return data;
  }

  async debug(message: string): Promise<void> {
    try {
      await this.client.post('/debug', { message });
    } catch (e) {
      // Ignore errors
    }
  }

  async updateTeam(teamId: string, updates: Partial<Team>): Promise<{ team: Team }> {
    const { data } = await this.client.put(`/admin/teams/${teamId}`, updates);
    return data;
  }

  // ============================================
  // AUDIT
  // ============================================

  async searchAudit(request: AuditSearchRequest): Promise<AuditSearchResponse> {
    const { data } = await this.client.post<AuditSearchResponse>('/audit/search', request);
    return data;
  }

  async getAuditSummary(teamId: string, startDate: string, endDate: string): Promise<any> {
    const { data } = await this.client.get(`/audit/summary/${teamId}?startDate=${startDate}&endDate=${endDate}`);
    return data;
  }

  async exportAudit(teamId: string, startDate: string, endDate: string, format: 'csv' | 'json'): Promise<Blob> {
    const { data } = await this.client.get(
      `/audit/export/${teamId}?startDate=${startDate}&endDate=${endDate}&format=${format}`,
      { responseType: 'blob' }
    );
    return data;
  }

  async getMessageAuditTrail(teamId: string, messageId: string): Promise<any> {
    const { data } = await this.client.get(`/audit/message/${teamId}/${messageId}`);
    return data;
  }

  async getShiftAuditHistory(teamId: string, userId: string, startDate: string, endDate: string): Promise<any> {
    const { data } = await this.client.get(
      `/audit/shifts/${teamId}/${userId}?startDate=${startDate}&endDate=${endDate}`
    );
    return data;
  }

  async getComplianceReport(teamId: string, startDate: string, endDate: string): Promise<any> {
    const { data } = await this.client.get(
      `/audit/compliance/${teamId}?startDate=${startDate}&endDate=${endDate}`
    );
    return data;
  }

  async getAuditActions(): Promise<{ actions: { value: string; label: string }[] }> {
    const { data } = await this.client.get('/audit/actions');
    return data;
  }

  async getAuditEntityTypes(): Promise<{ entityTypes: { value: string; label: string }[] }> {
    const { data } = await this.client.get('/audit/entity-types');
    return data;
  }
}



// Export singleton instance
export const api = new ApiService();
